import { useState, useCallback, useEffect } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { InformationCircleContained, Globe, ArrowRefresh3 } from 'blaze-ui/icons'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useUpdateBrandVoice'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useVisualStylePreviewCaption } from 'hooks/queries/visualStyles/useVisualStylePreviewCaption'
import { LogoVariant, VoiceParams, ContentPreferences } from 'types'
import { Attribute, PillsAttribute } from 'screens/BrandKit/BrandVoice/Attribute'
import { LanguageInput, DEFAULT_LANGUAGE } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { TextInputField } from 'components/Form/TextInputField'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { LikeIcon, CommentIcon, RepostIcon, SendIcon } from 'components/Previews/LinkedinPreview/icons'
import { Logos } from '../BrandKitConfirm/Logos/Logos'
import { PrimaryMarketLocations } from 'screens/BrandKits/components/ContentPreferences/PrimaryMarketLocations/PrimaryMarketLocations'
import { AudiencePreferences } from 'screens/BrandKits/components/ContentPreferences/AudiencePreferences/AudiencePreferences'
import { RepresentationPreferences } from 'screens/BrandKits/components/ContentPreferences/RepresentationPreferences/RepresentationPreferences'
import Styles from './BrandVisualStyleVoiceAndLogo.module.scss'
import cx from 'classnames'

interface BrandVisualStyleVoiceAndLogoProps {
  onNext: () => void
  onBack: () => void
  brandKitId: number
}

export const BrandVisualStyleVoiceAndLogo = ({ onNext, onBack, brandKitId }: BrandVisualStyleVoiceAndLogoProps) => {
  const { data: brandKitData } = useBrandKit(brandKitId)
  const [captionKey, setCaptionKey] = useState(0)

  const brandKit = brandKitData?.brand_kit
  const brandVoiceId = brandKit?.brand_voice?.id
  const brandVisualKitId = brandKit?.brand_visual_kit?.id
  const voiceParams = brandKit?.brand_voice?.voice_params
  const contentPreferences = brandKit?.content_preferences
  const primaryLogo = brandKit?.brand_visual_kit?.logos?.find((logo) => logo.logo_variant === LogoVariant.Original)

  const [localAudience, setLocalAudience] = useState<string>('')
  const [hasEditedAudience, setHasEditedAudience] = useState(false)

  useEffect(() => {
    if (!hasEditedAudience) {
      setLocalAudience(voiceParams?.audience?.join(', ') || '')
    }
  }, [voiceParams?.audience, hasEditedAudience])

  const { mutateAsync: updateBrandVoice } = useUpdateBrandVoice({ id: brandVoiceId || 0 })
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId })

  const { caption, isGenerating } = useVisualStylePreviewCaption({
    brandKitId,
    brandVoiceId: brandKit?.brand_voice?.id,
    brandKitName: brandKit?.name,
    enabled: !!brandKitId,
    truncate: false,
    regenerationKey: captionKey,
  })

  const handleRegenerateCaption = () => {
    setCaptionKey((prev) => prev + 1)
  }

  const handleVoiceChange = useCallback(
    async (field: keyof VoiceParams, value: string | string[]) => {
      if (!brandVoiceId) return

      const normalizedValue = Array.isArray(value) ? value : [value]

      await updateBrandVoice({
        voice_params: {
          ...voiceParams,
          [field]: normalizedValue,
        },
      })
    },
    [brandVoiceId, voiceParams, updateBrandVoice],
  )

  const handleContentPreferenceChange = useCallback(
    async (field: keyof ContentPreferences, value: string | string[] | number | undefined) => {
      await updateBrandKit({
        content_preferences: {
          ...contentPreferences,
          [field]: value,
        },
      })
    },
    [contentPreferences, updateBrandKit],
  )

  return (
    <div className={Styles.selectImageStep}>
      <div className={Styles.leftSide}>
        <div className={Styles.title}>
          <Heading level={1}>Confirm your Brand Voice & Logo</Heading>
          <Text color="var(--dark-60)">Based on your brand, we recommend:</Text>
        </div>

        <div className={Styles.content}>
          <div className={Styles.section}>
            <Heading level={4}>Logo</Heading>
            {brandVisualKitId && (
              <div className={Styles.logoWrapper}>
                <Logos
                  brandVisualKitId={brandVisualKitId}
                  showVariantSelector={false}
                  variantsToShow={[LogoVariant.Original]}
                  alignButtons="flex-end"
                />
              </div>
            )}
          </div>

          <div className={Styles.section}>
            <PrimaryMarketLocations
              locations={new Set(contentPreferences?.primary_market_locations || [])}
              onUpdate={(newSet) => handleContentPreferenceChange('primary_market_locations', Array.from(newSet))}
            />
          </div>

          <div className={Styles.section}>
            <AudiencePreferences
              minAge={contentPreferences?.audience_min_age}
              maxAge={contentPreferences?.audience_max_age}
              gender={contentPreferences?.audience_gender}
              onMinAgeUpdate={(age) => handleContentPreferenceChange('audience_min_age', age)}
              onMaxAgeUpdate={(age) => handleContentPreferenceChange('audience_max_age', age)}
              onGenderUpdate={(gender) => handleContentPreferenceChange('audience_gender', gender)}
            />
          </div>

          <div className={Styles.section}>
            <RepresentationPreferences
              ageRange={contentPreferences?.representation_age_range}
              gender={contentPreferences?.representation_gender}
              culture={contentPreferences?.representation_culture}
              details={contentPreferences?.representation_details}
              onAgeRangeUpdate={(ageRange) => handleContentPreferenceChange('representation_age_range', ageRange)}
              onGenderUpdate={(gender) => handleContentPreferenceChange('representation_gender', gender)}
              onCultureUpdate={(culture) => handleContentPreferenceChange('representation_culture', culture)}
              onDetailsUpdate={(details) => handleContentPreferenceChange('representation_details', details)}
              hideDetails={true}
            />
          </div>

          <div className={Styles.section}>
            <Attribute
              value={localAudience}
              onChange={(e) => {
                setLocalAudience(e.currentTarget.value)
                setHasEditedAudience(true)
              }}
              onBlur={(e) => handleVoiceChange('audience', e.currentTarget.value)}
              title="Audience"
              name="audience"
              description="Who you're talking to in your marketing."
            />
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={voiceParams?.tone || []}
              onChange={(value) => handleVoiceChange('tone', Array.from(value))}
              title="Tone"
              name="tone"
              description="How your brand sounds in every conversation."
              placeholder="Relaxed, Formal, Friendly"
            />
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={voiceParams?.emotions || []}
              onChange={(value) => handleVoiceChange('emotions', Array.from(value))}
              title="Emotion"
              name="emotion"
              description="The feelings your marketing should create."
              placeholder="Positive, Optimistic, Supportive"
            />
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={voiceParams?.character || []}
              onChange={(value) => handleVoiceChange('character', Array.from(value))}
              title="Character"
              name="character"
              description="The personality traits of your brand."
              placeholder="Professional, Friendly, Innovative"
            />
          </div>

          <div className={Styles.section}>
            <Heading level={4}>Content Language</Heading>
            <Text variant="metadata" color="var(--dark-60)">
              The primary language for your content.
            </Text>
            <div className={Styles.languageInputWrapper}>
              <LanguageInput
                key={contentPreferences?.language}
                languageValue={contentPreferences?.language || DEFAULT_LANGUAGE}
                onLanguageChange={(language) => handleContentPreferenceChange('language', language)}
                size="lg"
                matchReferenceWidth={true}
              />
            </div>
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={voiceParams?.language || []}
              onChange={(value) => handleVoiceChange('language', Array.from(value))}
              title="Language Style"
              name="language_style"
              description="The style and formality of language used."
              placeholder="Formal, Casual, Technical"
            />
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={voiceParams?.syntax || []}
              onChange={(value) => handleVoiceChange('syntax', Array.from(value))}
              title="Syntax"
              name="syntax"
              description="Specific writing guidelines and patterns."
              placeholder="Use casual language, Incorporate humor"
            />
          </div>

          <div className={Styles.section}>
            <PillsAttribute
              value={contentPreferences?.words_and_phrases_to_avoid || []}
              onChange={(value) => handleContentPreferenceChange('words_and_phrases_to_avoid', Array.from(value))}
              title="Words and Concepts to Avoid"
              name="words_to_avoid"
              description="Terms or phrases to exclude from content."
              placeholder="Add words to avoid"
            />
          </div>

          <div className={Styles.section}>
            <Heading level={4}>Default call-to-action</Heading>
            <Text variant="metadata" color="var(--dark-60)">
              Add the copy and link Blaze should use to drive traffic from your social posts
            </Text>

            <div className={Styles.socialCtaContainer}>
              <div className={Styles.socialCtaRow}>
                <div className={Styles.socialCtaInputs}>
                  <Text variant="label" color="var(--dark-80)">
                    Facebook, LinkedIn, X
                  </Text>
                  <TextInputField
                    placeholder="Learn More"
                    value={contentPreferences?.social_cta_copy || ''}
                    onChange={(e) => handleContentPreferenceChange('social_cta_copy', e.target.value)}
                  />
                  <TextInputField
                    placeholder="www.buynow.com"
                    value={contentPreferences?.social_cta_link || ''}
                    onChange={(e) => handleContentPreferenceChange('social_cta_link', e.target.value)}
                  />
                </div>
                <div className={Styles.socialCtaPreview}>
                  <Text variant="label" color="var(--dark-80)">
                    Caption Preview
                  </Text>
                  <img
                    src="https://res.cloudinary.com/almanac/image/upload/v1764594056/blaze_assets/brand_kit/social_preview_twdb8j.png"
                    alt="Facebook, LinkedIn, X preview"
                    className={Styles.socialCtaPreviewImage}
                  />
                </div>
              </div>

              <div className={Styles.socialCtaRow}>
                <div className={Styles.socialCtaInputs}>
                  <div className={Styles.socialCtaPlatformHeader}>
                    <Text variant="label" color="var(--dark-80)">
                      Instagram, YouTube
                    </Text>
                    <Tooltip label="Links are not supported on Instagram and YouTube">
                      <InformationCircleContained size={15} color="var(--dark-15)" style={{ cursor: 'pointer' }} />
                    </Tooltip>
                  </div>
                  <TextInputField
                    placeholder="Link in Bio"
                    value={contentPreferences?.social_without_links_cta_copy || ''}
                    onChange={(e) => handleContentPreferenceChange('social_without_links_cta_copy', e.target.value)}
                  />
                </div>
                <div className={Styles.socialCtaPreview}>
                  <Text variant="label" color="var(--dark-80)">
                    Caption Preview
                  </Text>
                  <img
                    src="https://res.cloudinary.com/almanac/image/upload/v1764594056/blaze_assets/brand_kit/social_video_preview_lcnjw4.png"
                    alt="Instagram, YouTube preview"
                    className={Styles.socialCtaPreviewImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={Styles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
          <Button variant="primary" size="lg" onPress={onNext}>
            Looks Good!
          </Button>
        </div>
      </div>

      <div className={Styles.transformedImageContainer}>
        <div className={Styles.previewContainer}>
          <div className={Styles.linkedInPreview} key={captionKey}>
            <div className={Styles.previewHeader}>
              <div className={Styles.profileImage}>
                {primaryLogo ? (
                  <img src={primaryLogo.media_file.source_url} alt="Brand logo" />
                ) : (
                  <div className={Styles.profileImagePlaceholder}>{brandKit?.name?.charAt(0) || 'B'}</div>
                )}
              </div>
              <div className={Styles.profileInfo}>
                <div className={Styles.profileName}>{brandKit?.name || 'Brand Name'}</div>
                <div className={Styles.profileSubtitle}>Head of Awesome</div>
                <div className={Styles.profileMeta}>
                  1m • Edited • <Globe size={9} color="rgba(255, 255, 255, 0.6)" />
                </div>
              </div>
            </div>

            <div className={Styles.previewContent}>
              {isGenerating ? (
                <div className={Styles.shimmerContainer}>
                  <div className={Styles.shimmerLine} />
                  <div className={Styles.shimmerLine} />
                  <div className={Styles.shimmerLine} style={{ width: '80%' }} />
                </div>
              ) : (
                <div className={Styles.captionText}>{caption || 'Your brand voice caption will appear here'}</div>
              )}
            </div>

            <div className={Styles.previewFooter}>
              <div className={Styles.footerAction}>
                <LikeIcon size={9} color="#666" />
                <span>Like</span>
              </div>
              <div className={Styles.footerAction}>
                <CommentIcon size={9} color="#666" />
                <span>Comment</span>
              </div>
              <div className={Styles.footerAction}>
                <RepostIcon size={9} color="#666" />
                <span>Repost</span>
              </div>
              <div className={Styles.footerAction}>
                <SendIcon size={9} color="#666" />
                <span>Send</span>
              </div>
            </div>
          </div>

          <Button
            className={cx(Styles.updatePreviewButton, { [Styles.spinning]: isGenerating })}
            variant="ghost"
            size="sm"
            frontIcon={ArrowRefresh3}
            onPress={handleRegenerateCaption}
            isDisabled={isGenerating}
          >
            Update Preview
          </Button>
        </div>
      </div>
    </div>
  )
}
